import type { EnvironmentId } from "@cmmd-forge/contracts";

import {
  isPlacementChoiceOffered,
  resolvePlacementEligibleEnvironments,
  type PlacementEnvironment,
  type ProjectPlacement,
} from "~/logicalProjectPlacement";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { SettingsRow, SettingsSection } from "../settings/settingsLayout";

/** The one string both the control and its tests address the chooser by. */
const PLACEMENT_CONTROL_LABEL = "Project environment";

export interface ProjectEnvironmentPlacementSectionProps {
  readonly environments: readonly PlacementEnvironment[];
  readonly placement: ProjectPlacement;
  readonly onSelectEnvironment: (environmentId: EnvironmentId) => void;
}

/**
 * Which Environment this Project lives on — shown only to a Seat that owns
 * more than one.
 *
 * The whole surface is behind `isPlacementChoiceOffered`, so for a Seat with a
 * single Environment this renders nothing: the first Environment silently
 * absorbs every Project and placement is a question that Seat is never asked.
 *
 * That means the control is currently unreachable in the product, and it is
 * meant to be. Forge cannot yet enumerate a Seat's Environments — it receives
 * only `cmmd-runtime:` identifiers, which name a Sprite per Project rather than
 * an Environment (FOR-32652 exposes Environment identity; FOR-32649 removes the
 * per-project keying) — so the mounted list is empty and every Seat is a
 * single-Environment Seat. Both branches are held by
 * `ProjectEnvironmentPlacementSection.test.tsx`, so the visible branch is
 * proven rather than merely written.
 */
export function ProjectEnvironmentPlacementSection(props: ProjectEnvironmentPlacementSectionProps) {
  if (!isPlacementChoiceOffered(props.environments)) return null;

  const eligible = resolvePlacementEligibleEnvironments(props.environments);
  const placedOn =
    props.placement.kind === "assigned" || props.placement.kind === "absorbed"
      ? props.placement.environmentId
      : null;
  const selected = eligible.find((option) => option.environmentId === placedOn) ?? null;

  return (
    <SettingsSection title="Environment">
      <SettingsRow
        title="Runs on"
        description="Which of your Environments holds this project's checkout."
        control={
          <Select
            value={selected?.environmentId ?? ""}
            onValueChange={(value) => props.onSelectEnvironment(String(value) as EnvironmentId)}
          >
            <SelectTrigger className="max-w-64" aria-label={PLACEMENT_CONTROL_LABEL}>
              {/* No fallback to the first Environment: a Project the customer
                  has not placed must read as unplaced, because a guess they
                  cannot predict is a billing dispute once Environments are
                  purchasable. */}
              <SelectValue>{selected?.label ?? "Choose an Environment"}</SelectValue>
            </SelectTrigger>
            <SelectPopup align="end" alignItemWithTrigger={false}>
              {eligible.map((option) => (
                <SelectItem key={option.environmentId} hideIndicator value={option.environmentId}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>
        }
      />
    </SettingsSection>
  );
}
